import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Tag, Select, Input, Space, Row, Col, Statistic, Typography, Tooltip, message,
} from 'antd';
import {
  RobotOutlined, DollarOutlined, ThunderboltOutlined, MessageOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { chatbotLogAPI } from '../services/api';
import { useFitTableHeight } from '../hooks/useViewportFit';

const { Text } = Typography;

interface ChatbotLogRow {
  id: number;
  username: string;
  question: string;
  answer: string;
  route: string;
  intent: string | null;
  model: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  cost_usd: number | null;
  duration_ms: number | null;
  created_at: string | null;
}

interface ModelStat {
  model: string;
  turns: number;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: number;
}

interface LogStats {
  days: number;
  total_turns: number;
  by_route: Record<string, number>;
  by_model: ModelStat[];
  total_cost_usd: number;
}

const ROUTE_META: Record<string, { label: string; color: string }> = {
  llm: { label: 'AI 응답', color: 'purple' },
  rule: { label: '규칙', color: 'blue' },
  cache: { label: '캐시', color: 'cyan' },
  search_fallback: { label: '검색 폴백', color: 'orange' },
  empty: { label: '무결과', color: 'default' },
};

const ChatbotLogPage: React.FC = () => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const [rows, setRows] = useState<ChatbotLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [days, setDays] = useState(30);
  const [route, setRoute] = useState<string>();
  const [username, setUsername] = useState('');

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const [logRes, statRes] = await Promise.all([
        chatbotLogAPI.list({ page: p, page_size: pageSize, days, route: route || undefined, username: username || undefined }),
        chatbotLogAPI.stats({ days }),
      ]);
      setRows(logRes.data.items || []);
      setTotal(logRes.data.total || 0);
      setPage(p);
      setStats(statRes.data);
    } catch {
      message.error('챗봇 이력 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [days, route, username, pageSize]);

  useEffect(() => { load(1); }, [load]);

  const llmTurns = stats?.by_route?.llm || 0;
  const totalTokens = (stats?.by_model || []).reduce((s, m) => s + m.prompt_tokens + m.completion_tokens, 0);

  const columns = [
    {
      title: '일시', dataIndex: 'created_at', width: 150,
      render: (v: string | null) => v ? v.replace('T', ' ').slice(0, 19) : '-',
    },
    { title: '사용자', dataIndex: 'username', width: 90 },
    {
      title: '경로', dataIndex: 'route', width: 100,
      render: (v: string) => {
        const m = ROUTE_META[v] || { label: v, color: 'default' };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: '질문', dataIndex: 'question', ellipsis: true,
      render: (v: string) => <Text style={{ fontSize: 13 }}>{v}</Text>,
    },
    {
      title: '인텐트/모델', width: 190, ellipsis: true,
      render: (_: unknown, r: ChatbotLogRow) => r.route === 'rule'
        ? <Tag>{r.intent || '-'}</Tag>
        : (r.model ? <Tooltip title={r.model}><Text code style={{ fontSize: 11 }}>{r.model.split('/').pop()}</Text></Tooltip> : '-'),
    },
    {
      title: '토큰(입/출)', width: 110, align: 'right' as const,
      render: (_: unknown, r: ChatbotLogRow) =>
        r.prompt_tokens != null ? `${r.prompt_tokens.toLocaleString()} / ${(r.completion_tokens || 0).toLocaleString()}` : '-',
    },
    {
      title: '비용', dataIndex: 'cost_usd', width: 95, align: 'right' as const,
      render: (v: number | null) => v != null ? `$${v.toFixed(5)}` : '-',
    },
    {
      title: '응답', dataIndex: 'duration_ms', width: 80, align: 'right' as const,
      render: (v: number | null) => v != null ? `${(v / 1000).toFixed(1)}초` : '-',
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space align="center">
          <RobotOutlined style={{ fontSize: 20, color: '#7c3aed' }} />
          <Text strong style={{ fontSize: 17 }}>챗봇 이력</Text>
          <Text type="secondary">질문/답변 · 적용 모델 · 토큰 사용량 기록 (성능 개선 분석용)</Text>
        </Space>
      </div>

      {/* 요약 통계 */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title={`총 질문 (최근 ${days}일)`} value={stats?.total_turns ?? 0}
            suffix="건" prefix={<MessageOutlined style={{ color: '#6366f1' }} />} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="AI 응답" value={llmTurns} suffix="건"
            prefix={<RobotOutlined style={{ color: '#7c3aed' }} />} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="총 토큰" value={totalTokens}
            prefix={<ThunderboltOutlined style={{ color: '#f59e0b' }} />} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="총 비용(USD)" value={stats?.total_cost_usd ?? 0} precision={4}
            prefix={<DollarOutlined style={{ color: '#10b981' }} />} /></Card>
        </Col>
      </Row>

      {/* 모델별 집계 */}
      {(stats?.by_model?.length ?? 0) > 0 && (
        <Card size="small" style={{ marginBottom: 16 }} title="모델별 사용량">
          <Space wrap>
            {stats!.by_model.map((m) => (
              <Tag key={m.model} color="purple" style={{ padding: '4px 10px', fontSize: 12 }}>
                {m.model} — {m.turns}턴 · 입력 {m.prompt_tokens.toLocaleString()} / 출력 {m.completion_tokens.toLocaleString()} tok · ${m.cost_usd.toFixed(4)}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      {/* 필터 + 목록 */}
      <div ref={cardWrapRef}>
      <Card size="small">
        <Space wrap style={{ marginBottom: 12 }}>
          <Select value={days} onChange={setDays} style={{ width: 120 }}
            options={[{ value: 7, label: '최근 7일' }, { value: 30, label: '최근 30일' }, { value: 90, label: '최근 90일' }]} />
          <Select allowClear placeholder="경로" value={route} onChange={setRoute} style={{ width: 130 }}
            options={Object.entries(ROUTE_META).map(([v, m]) => ({ value: v, label: m.label }))} />
          <Input.Search allowClear placeholder="사용자" style={{ width: 150 }}
            onSearch={(v) => setUsername(v.trim())} />
          <ReloadOutlined onClick={() => load(page)} style={{ cursor: 'pointer', color: '#64748b' }} />
        </Space>

        <div ref={tableWrapRef}>
        <Table<ChatbotLogRow>
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ y: bodyHeight }}
          pagination={{
            current: page, total, pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            onChange: (p, ps) => { if (ps !== pageSize) setPageSize(ps); else load(p); },
            showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
          }}
          expandable={{
            expandedRowRender: (r) => (
              <div style={{ display: 'grid', gap: 8, padding: '4px 8px' }}>
                <div>
                  <Text strong style={{ fontSize: 12, color: '#6366f1' }}>Q. </Text>
                  <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{r.question}</Text>
                </div>
                <div style={{ background: '#faf5ff', borderRadius: 8, padding: '8px 12px', border: '1px solid #ede9fe' }}>
                  <Text strong style={{ fontSize: 12, color: '#7c3aed' }}>A. </Text>
                  <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{r.answer}</Text>
                </div>
              </div>
            ),
          }}
        />
        </div>
      </Card>
      </div>
    </div>
  );
};

export default ChatbotLogPage;
